import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LockIcon from '@mui/icons-material/Lock';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import type { Section } from '../../../../types/tender';
import { SectionDragHandle } from '../SortableSectionItem';
import {
  countWords,
  extractTitleTags,
  getStatusColor,
  getStatusLabel,
  getStatusSx,
  getUserAvatarColor,
} from '../utils';

interface SectionHeaderRowProps {
  section: Section;
  account: import('../../../../types/auth').User | null;
  isOwner?: boolean;
  canManageAssignments: boolean;
  canBreakLock: boolean;
  isHistoryComparison?: boolean;
  statusAnchorEl: null | HTMLElement;
  openStatusMenu: boolean;
  handleStatusClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleStatusClose: () => void;
  handleStatusSelect: (status: string) => void;
  handlers: {
    handleUnlock: (section: Section) => Promise<void>;
    handleOpenAssignDialog: (section: Section) => void;
    assign: (
      section: Section,
      user: Record<string, unknown> | null,
      permissions?: string[]
    ) => Promise<void>;
    handleDeleteClick: (section: Section) => void;
  };
}

const SectionHeaderRow: React.FC<SectionHeaderRowProps> = ({
  section,
  account,
  isOwner,
  canManageAssignments,
  canBreakLock,
  isHistoryComparison,
  statusAnchorEl,
  openStatusMenu,
  handleStatusClick,
  handleStatusClose,
  handleStatusSelect,
  handlers,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        pr: 1, // Reduced padding right
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        gap: 0.5, // Reduced gap between elements (icons)
      }}
    >
      <SectionDragHandle />

      <Box sx={{ flexGrow: 1, minWidth: 0, mr: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold" component="span">
          {extractTitleTags(section.title).cleanTitle}
        </Typography>

        <Typography
          component="span"
          variant="body2"
          color="text.secondary"
          sx={{
            fontWeight: 'normal',
            fontSize: '0.875rem',
            ml: 1,
            whiteSpace: 'nowrap',
          }}
        >
          ({countWords(section.content || '').toLocaleString()} words)
        </Typography>

        {/* Lock Icon */}
        {section.lockedBy && section.lockedBy.uid !== account?.uid && (
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              ml: 1,
              color: '#812A16', // Rust color
              verticalAlign: 'middle',
              gap: 0.5,
            }}
          >
            <Tooltip title={`Locked for editing by ${section.lockedBy.displayName}`}>
              <LockIcon sx={{ fontSize: '1rem' }} />
            </Tooltip>

            <Typography
              variant="caption"
              sx={{ fontWeight: 'bold', color: theme.palette.bidlogiq.brick }}
            >
              {section.lockedBy.displayName}
            </Typography>

            {canBreakLock && (
              <Button
                variant="text"
                color="inherit"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handlers.handleUnlock(section);
                }}
                sx={{
                  minWidth: 'auto',
                  p: 0,
                  ml: 0.5,
                  fontSize: '0.7rem',
                  textDecoration: 'underline',
                  '&:hover': { bgcolor: 'transparent', textDecoration: 'none' },
                }}
              >
                Unlock
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Enterprise Badges */}
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mr: 1 }}>
        {section.cross_boundary_suggestion && (
          <Tooltip title="This AI edit was triggered by a team member working on another section to resolve an internal consistency issue.">
            <Chip
              label="AI Consistency"
              size="small"
              sx={{
                fontWeight: 'bold',
                bgcolor: theme.palette.warning.main,
                color: theme.palette.warning.contrastText,
              }}
            />
          </Tooltip>
        )}

        {section.assignedTo && section.status !== 'reviewed' && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={`Assigned to ${section.assignedTo.displayName}`}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  ...getUserAvatarColor(section.assignedTo.displayName || ''),
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (canManageAssignments) {
                    handlers.handleOpenAssignDialog(section);
                  }
                }}
              >
                {section.assignedTo.displayName?.charAt(0) || 'U'}
              </Avatar>
            </Tooltip>
            {canManageAssignments && (
              <Tooltip title="Remove Assignment">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlers.assign(section, null);
                  }}
                  sx={{
                    ml: -1,
                    mt: -1.5,
                    bgcolor: 'background.paper',
                    width: 16,
                    height: 16,
                    border: '1px solid',
                    borderColor: 'divider',
                    zIndex: 1,
                    '&:hover': {
                      bgcolor: 'error.light',
                      color: 'white',
                      borderColor: 'error.main',
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: '10px' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {!section.assignedTo && section.status !== 'reviewed' && canManageAssignments && (
          <Tooltip title="Assign to team member">
            <IconButton
              size="small"
              aria-label="Assign to team member"
              onClick={(e) => {
                e.stopPropagation();
                handlers.handleOpenAssignDialog(section);
              }}
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
              }}
            >
              <PersonAddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {section.reviewedBy && (
          <Tooltip
            title={
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" fontWeight="bold">
                  Reviewed by {section.reviewedBy.displayName}
                </Typography>
                <Typography variant="caption" display="block">
                  {section.reviewedBy.email}
                </Typography>
                <Typography variant="caption" display="block">
                  {(() => {
                    const dateVal =
                      section.reviewedBy.at ||
                      (section.status === 'reviewed' ? section.updatedAt : null);
                    if (!dateVal) {
                      return 'Date not available';
                    }
                    if (typeof dateVal === 'object' && dateVal !== null && 'seconds' in dateVal) {
                      return new Date(
                        (dateVal as { seconds: number }).seconds * 1000
                      ).toLocaleString();
                    }
                    return new Date(dateVal).toLocaleString();
                  })()}
                </Typography>
              </Box>
            }
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.875rem',
                ...getUserAvatarColor(section.reviewedBy.displayName || ''),
                border: '2px solid',
                borderColor: 'success.main',
              }}
            >
              {section.reviewedBy.displayName?.charAt(0) || 'R'}
            </Avatar>
          </Tooltip>
        )}
      </Stack>

      <Chip
        label={getStatusLabel(
          section.status,
          !!section.suggested_content,
          !!section.assignedTo,
          isHistoryComparison
        )}
        color={getStatusColor(section.status, !!section.assignedTo)}
        size="medium"
        variant={section.status === 'reviewed' ? 'filled' : 'filled'}
        onClick={handleStatusClick}
        deleteIcon={<KeyboardArrowDownIcon />}
        onDelete={handleStatusClick}
        sx={{
          ...getStatusSx(
            theme,
            section.status,
            !!section.suggested_content,
            !!section.assignedTo,
            isHistoryComparison
          ),
          flexShrink: 0,
        }}
      />

      <Menu
        anchorEl={statusAnchorEl}
        open={openStatusMenu}
        onClose={handleStatusClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => handleStatusSelect('pending')}>Pending</MenuItem>
        <MenuItem onClick={() => handleStatusSelect('in_progress')}>In Progress</MenuItem>
        <MenuItem onClick={() => handleStatusSelect('complete')}>Complete</MenuItem>
        {isOwner && <MenuItem onClick={() => handleStatusSelect('reviewed')}>Reviewed</MenuItem>}
      </Menu>

      <Tooltip title="Delete this section?">
        <IconButton
          size="small"
          color="default"
          onClick={(e) => {
            e.stopPropagation();
            handlers.handleDeleteClick(section);
          }}
          sx={{ ml: 1 }}
          aria-label="delete section"
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default SectionHeaderRow;
